/**
 * Progressive-web-app install plumbing.
 *
 * The browser fires `beforeinstallprompt` once, early in page load — long before
 * a user reaches the onboarding tour — and only lets you call `.prompt()` on
 * that exact event object later, in response to a user gesture. So we capture
 * the event at startup (installPwaCapture(), called from main.tsx), stash it,
 * and hand it to the install UI when it's ready.
 *
 * iOS Safari never fires this event and exposes no programmatic install: there
 * the user adds the app via Share -> Add to Home Screen. We detect that platform
 * so the install step can show instructions instead of a (non-functional) button.
 *
 * All access is best-effort and guarded for SSR/non-browser contexts so this
 * module is safe to import from anywhere, including under jsdom in tests.
 */

import { useSyncExternalStore } from 'react';

/** The non-standard install-prompt event. Not in the DOM lib typings. */
export interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferredPrompt: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) {
    listener();
  }
}

/** Subscribe to install-availability changes. Returns an unsubscribe fn. */
export function subscribeInstall(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Wire up the global install listeners. Call exactly once at app startup
 * (before the user can reach any install UI). No-op outside the browser.
 */
export function installPwaCapture(): void {
  if (typeof window === 'undefined') {
    return;
  }
  window.addEventListener('beforeinstallprompt', (event) => {
    // Stop Chromium's mini-infobar; we drive the prompt ourselves from the tour.
    event.preventDefault();
    deferredPrompt = event as BeforeInstallPromptEvent;
    emit();
  });
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    emit();
  });
}

export function getDeferredPrompt(): BeforeInstallPromptEvent | null {
  return deferredPrompt;
}

/** True when the app is already running as an installed standalone app. */
export function isStandalone(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  // jsdom (tests) and legacy browsers lack matchMedia; the cast redeclares
  // what the DOM lib overpromises.
  const { matchMedia } = window as { matchMedia?: typeof window.matchMedia };
  if (matchMedia?.('(display-mode: standalone)').matches === true) {
    return true;
  }
  // iOS Safari exposes navigator.standalone rather than the display-mode query.
  return (window.navigator as { standalone?: boolean }).standalone === true;
}

/** True on iOS/iPadOS Safari, which installs via Share -> Add to Home Screen. */
export function isIosSafari(): boolean {
  if (typeof navigator === 'undefined') {
    return false;
  }
  const ua = navigator.userAgent;
  const touchPoints = (navigator as { maxTouchPoints?: number }).maxTouchPoints ?? 0;
  const isIos =
    /iphone|ipad|ipod/i.test(ua) ||
    // iPadOS 13+ reports a Mac UA but is touch-capable.
    (/macintosh/i.test(ua) && touchPoints > 1);
  if (!isIos) {
    return false;
  }
  // Exclude Chrome (CriOS), Firefox (FxiOS) and Edge (EdgiOS) on iOS — those
  // can't add to the home screen.
  return /safari/i.test(ua) && !/crios|fxios|edgios/i.test(ua);
}

/**
 * Whether to offer the in-tour install step: the app isn't already installed,
 * and we can either fire a captured prompt (Android/Chromium) or guide the user
 * through the manual iOS flow.
 */
export function canOfferInstall(): boolean {
  if (isStandalone()) {
    return false;
  }
  return deferredPrompt !== null || isIosSafari();
}

/**
 * Trigger the native install prompt. Resolves to the user's choice, or null when
 * no prompt is available (e.g. iOS). The captured event is single-use, so it is
 * cleared afterwards regardless of outcome.
 */
export async function promptInstall(): Promise<'accepted' | 'dismissed' | null> {
  const event = deferredPrompt;
  if (!event) {
    return null;
  }
  await event.prompt();
  const { outcome } = await event.userChoice;
  deferredPrompt = null;
  emit();
  return outcome;
}

/** Reactive view of install availability for React components. */
export function usePwaInstall(): {
  canPrompt: boolean;
  isIos: boolean;
  promptInstall: typeof promptInstall;
} {
  const canPrompt = useSyncExternalStore(
    subscribeInstall,
    () => deferredPrompt !== null,
    () => false,
  );
  return { canPrompt, isIos: isIosSafari(), promptInstall };
}
